<script setup lang="ts">
import { computed, ref, reactive } from 'vue';
import type { SurveyProgressAnswer, SurveyQuestionModel } from '@/types/model';
import type { ISurveyButton, ISurveySlider, ITheSurveyMultipleChoiceComponent, ITheSurveyPageComponent } from '@/types/components';
import TheSurveyMultipleChoice from './TheSurveyMultipleChoice.vue';
import SurveyButton from './SurveyButton.vue';
import SurveySlider from './SurveySlider.vue';
import { useSurveyStore } from '@/store';

import { SurveyComponentQuestionTypeEnum } from '@/constants/survey';

const props = defineProps({
  showHeaderTextBold: {
    type: Boolean,
    default: false
  }
});
const surveyModule = useSurveyStore();

const emit = defineEmits(['onNextClick', 'onDeclineClick', 'update:selectedValues']);

const multipleChoiceAnswers = ref<ITheSurveyMultipleChoiceComponent[]>([]);
const sliderAnswers = ref<ISurveySlider[]>([]);
const buttonAnswers = ref<ISurveyButton[]>([]);
const currentPage = computed(() => surveyModule.currentPage);

// Initialize selectedValues with empty objects for the multiple choice depths
let selectedValues = reactive({ 0:{}, 1: {}, 2: {} });
selectedValues[currentPage.value.renderingConfig.order] = {};

const isMultipleChoice = (question: SurveyQuestionModel) => {
  return (
    question.renderingConfig.type === SurveyComponentQuestionTypeEnum.choice &&
    question.permittedAnswers.length !== 0
  );
};

const isButton = (question: SurveyQuestionModel) => {
  return (
    question.renderingConfig.type === SurveyComponentQuestionTypeEnum.button &&
    question.permittedAnswers.length !== 0
  );
};

const isSlider = (question: SurveyQuestionModel) => {
  return question.renderingConfig.type === SurveyComponentQuestionTypeEnum.slider;
};

const validate = async () => {
  let valid = true;
  if (multipleChoiceAnswers.value) {
    const results = await Promise.all(
      multipleChoiceAnswers.value.map((mc) => mc.validate())
    );
    valid = results.every((valid) => valid);
  }
  return valid;
};

const getResponses = () => {
  let responses: SurveyProgressAnswer[] = [];

  let multiplechoiceAnswers: SurveyProgressAnswer[] = [];
  let sliderAnswersResponses: SurveyProgressAnswer[] = [];
  let buttonAnswersResponses: SurveyProgressAnswer[] = [];

  currentPage.value.questions.forEach((question) => {
    if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.choice) {
      multiplechoiceAnswers = multipleChoiceAnswers.value
        .map((mca) => mca.getResponses())
        .flat();
    } else if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.slider) {
      sliderAnswersResponses = sliderAnswers.value.map((sa) => sa.getResponse());
    } else if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.button) {
      buttonAnswersResponses = buttonAnswers.value.map((ba) => ba.getResponse());
    }
  });

  responses = [...multiplechoiceAnswers, ...sliderAnswersResponses, ...buttonAnswersResponses];

  return responses;
};

defineExpose({
  getResponses,
  validate
});
</script>

<template>
  <div v-for="question in currentPage.questions"
       class="row wizard__question"
       :key="question.questionId">
    <div class="col">

      <!-- Question Image -->
      <template v-if="question.renderingConfig.imageUrl">
        <div class="row justify-center q-pb-xl">
          <q-img width="50%" :src="question.renderingConfig.imageUrl" />
        </div>
      </template>

      <!-- Question Header -->
      <template v-if="question.renderingConfig.showHeader">
        <h3 v-if="question.questionHeader"
            v-html="question.questionHeader"
            class="wizard__question-header" />
      </template>

      <!-- Question Text -->
      <p v-if="question.questionText"
         v-html="question.questionText"
         :class="['wizard__question-text', { 'text-weight-black': showHeaderTextBold && question.questionHeader }]" />

      <!-- Question Sub-Text -->
      <p v-if="question.questionSubText"
         v-html="question.questionSubText"
         class="wizard__question-subtext" />

      <!-- Footer -->
      <p v-if="currentPage.footer"
         v-html="currentPage.footer"
         class="wizard__footer" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
  .wizard {
    // Question
    &__question { // .row
      text-align: center;

      &:not(:last-child) {
        margin-block-end: 30px;
      }
      // Header
      &-header { // <h3>
        margin-block-end: map.get($font-sizes, '22');
      }
      // Text
      &-text { // <p>
        font-size: map.get($font-sizes, '18');
      }
    }
    // Footer
    &__footer { // <p>
      color: $border-clr;
      text-align: center;
      padding-block-start: 50px;
    }
  }
</style>
